// client/address-controller.ts
import { A } from "@adornis/base/env-info.js";
import { logger } from "@adornis/base/logging.js";
import "@adornis/chemistry/controllers/chemistry-controller.js";
import "@adornis/formfield/form-field.js";
import "lit";
import { Directive, directive } from "lit/directive.js";
import { Subscription, fromEvent, takeUntil } from "rxjs";
var addressFieldTypeMap = {
  city: "locality",
  address: "geocode|establishment",
  zip: "postal_code",
  country: "country",
  province: "administrative_area_level_1"
};
var addressFieldTypeIndex = {
  address: 0,
  zip: 1,
  city: 2,
  province: 3,
  country: 4
};
var AddressDirective = class extends Directive {
  constructor(partInfo) {
    super(partInfo);
    const element = partInfo.element;
    if (!element || !element.value)
      throw new Error("formfield directive can only be used as ElementPart and only on FormFields");
    this._element = element;
  }
  render(controller, type) {
    controller.updateFormFieldFromWithinDirective(type, this._element);
  }
};
var addressDirective = directive(AddressDirective);
var AddressController = class {
  constructor(host, options = {}) {
    this.options = options;
    this.formFields = /* @__PURE__ */ new Map();
    this._last = {
      suggestions: [],
      sessionID: A.getGloballyUniqueID()
    };
    this.street = this.address;
    (this.host = host).addController(this);
  }
  zip() {
    return addressDirective(this, "zip");
  }
  city() {
    return addressDirective(this, "city");
  }
  address() {
    return addressDirective(this, "address");
  }
  country() {
    return addressDirective(this, "country");
  }
  province() {
    return addressDirective(this, "province");
  }
  /**
   * @deprecated stub for broken implements typing
   */
  hostConnected() {
  }
  /**
   * @deprecated stub for broken implements typing
   */
  hostDisconnected() {
  }
  /**
   * @deprecated just for internal use
   */
  updateFormFieldFromWithinDirective(type, formField) {
    const hasFormKey = this.formFields.has(type);
    const fieldAlreadyKnown = hasFormKey && this.formFields.get(type).find((entry) => entry.field === formField);
    if (!fieldAlreadyKnown) {
      this._removeField(formField);
    }
    const subscription = new Subscription();
    if (!hasFormKey)
      this.formFields.set(type, [{ field: formField, subscription }]);
    else if (!fieldAlreadyKnown)
      this.formFields.get(type).push({ field: formField, subscription });
    if (!fieldAlreadyKnown) {
      this._attachEventListenersToFormField(type, formField, subscription);
    }
  }
  _attachEventListenersToFormField(type, formField, subscription) {
    if (formField.renderSelected) {
      formField.search = async (search) => {
        const results = await this._search(search, formField, type);
        return formField.mapSearch ? formField.mapSearch(results) : results;
      };
      formField.renderString = (value, index) => {
        return this._renderString(type, value, index);
      };
      formField.debounceInput = 500;
    }
    subscription.add(
      fromEvent(formField, "value-picked").pipe(takeUntil(this.host.disconnected)).subscribe((e) => this._handleValuePicked(type, formField, e))
    );
  }
  _filterFields(filter) {
    const currentFields = Array.from(this.formFields.entries());
    for (const [key, fields] of currentFields) {
      const removed = [];
      const filteredFields = fields.filter((f) => {
        const toFilter = filter(f.field);
        if (!toFilter)
          removed.push(f);
        return toFilter;
      });
      for (const entry of removed)
        entry.subscription.unsubscribe();
      if (filteredFields.length) {
        this.formFields.set(key, filteredFields);
      } else {
        this.formFields.delete(key);
      }
    }
  }
  _removeField(field) {
    this._filterFields((f) => f !== field);
  }
  _cleanupDisconnectedInputs() {
    this._filterFields((f) => f.isConnected);
  }
  _findLastSuggestion(type, value) {
    return this._last.suggestions.find((result) => result.formattedAddress === value);
  }
  async _handleValuePicked(type, formField, event) {
    if (event.detail.fromAddressController || event.detail.manual)
      return;
    this._cleanupDisconnectedInputs();
    const suggestion = this._findLastSuggestion(type, event.detail.value);
    if (!suggestion)
      return console.warn(
        "Could not find a lastSuggestion for type",
        type,
        "and picked",
        event.detail.value,
        "last Suggestions:",
        this._last.suggestions
      );
    await this._fillAddress(suggestion, type);
    this._last.interactedInput = void 0;
  }
  async _search(search, formField, type) {
    if (this._last.autoFill === search)
      return this._last.suggestions.map((sugg) => sugg.formattedAddress);
    if (this._last.interactedInput !== formField) {
      this._last.sessionID = A.getGloballyUniqueID();
      this._last.interactedInput = formField;
    }
    const results = await this._completeAddress(search, type);
    this._last.suggestions = results;
    return results.map((result) => result.formattedAddress);
  }
  _renderString(type, value, index) {
    if (index === void 0)
      return value ?? "";
    const suggestion = this._last.suggestions[index];
    return suggestion?.formattedAddress ?? "";
  }
  async _completeAddress(search, type) {
    if (!search)
      return [];
    const url = `/proxy/address/autocomplete`;
    const params = new URLSearchParams();
    params.append("search", search);
    params.append("session", this._last.sessionID);
    params.append("type", addressFieldTypeMap[type]);
    if (this.options.countries)
      params.append("countries", this.options.countries.join(","));
    if (this.options.language)
      params.append("language", this.options.language);
    const rawResponse = await fetch(url + "?" + params.toString());
    const response = await rawResponse.json();
    const suggestions = [];
    for (const result of response.predictions) {
      const suggestion = {
        placeID: result.place_id,
        formattedAddress: result.description
      };
      suggestions.push(suggestion);
    }
    return suggestions;
  }
  async _fillAddress(suggestion, selectedFrom) {
    const url = `/proxy/address/details`;
    const params = new URLSearchParams();
    params.append("placeID", suggestion.placeID);
    params.append("session", this._last.sessionID);
    if (this.options.language)
      params.append("language", this.options.language);
    const rawResponse = await fetch(url + "?" + params.toString());
    const response = await rawResponse.json();
    if (!response.result.address_components)
      return;
    const autoFillData = {
      address: "",
      city: "",
      country: "",
      province: "",
      zip: ""
    };
    let street = "";
    let streetNumber = "";
    for (const component of response.result.address_components) {
      switch (component.types[0]) {
        case "route":
        case "street_address":
        case "town_square":
          street = component.long_name;
          break;
        case "street_number":
          streetNumber = component.long_name;
          break;
        case "postal_code":
          autoFillData.zip = component.long_name;
          break;
        case "locality":
          autoFillData.city = component.long_name;
          break;
        case "country":
          autoFillData.country = component.short_name;
          break;
        case "administrative_area_level_1":
          autoFillData.province = component.long_name;
          break;
        default:
          logger.warn("unhandled part", component.types);
      }
    }
    if (street || streetNumber)
      autoFillData.address = `${street} ${streetNumber}`;
    this._last.autoFill = autoFillData[selectedFrom];
    for (const [fieldType, fieldInfo] of this.formFields) {
      if (this._shouldFillField(fieldType, selectedFrom)) {
        for (const field of fieldInfo) {
          field.field.setValue ? field.field.setValue(autoFillData[fieldType]) : field.field.value.next(autoFillData[fieldType]);
          field.field.dispatchEvent(
            new CustomEvent("value-picked", {
              detail: { value: autoFillData[fieldType], fromAddressController: true }
            })
          );
        }
      }
    }
  }
  _shouldFillField(fieldType, selectedFrom) {
    return addressFieldTypeIndex[fieldType] >= addressFieldTypeIndex[selectedFrom];
  }
};
export {
  AddressController
};
